import React from 'react';
import { Stack } from '@mui/material';
import { useIsFeatureEnabled } from '@innovyze/stylovyze';

import { useGlobalization } from '@Translations';
import { WeibullFormulation } from '@Types';
import { WeibullFallbackBanner } from '@Components/deteriorationModel/utils/WeibullFallbackBanner';
import { WeibullFitNotes } from '@Components/deteriorationModel/utils/WeibullFitNotes';
import { getWeibullDeclineNote } from '@Components/deteriorationModel/utils/weibullDecline';
import type { useCurveConfig } from './hooks';

interface CurveFitNotesProps {
	curveState: ReturnType<typeof useCurveConfig>;
}

/**
 * Explains the previewed curve: whether it is a substitute for an unavailable formulation, whether
 * it is usable at all, and which coefficients produced it. The last two are shared with the results
 * tab via `WeibullFitNotes`; only the formulation-substitution message is specific to the preview,
 * where the 2P/3P toggle lives.
 *
 * Lives under the chart rather than beside the method picker because these messages describe the
 * curve, and because stacking them into the picker pushed most of the curve methods out of view.
 * "No curve fitted at all" stays with the picker — that is a statement about the method rather
 * than the curve, and it makes everything here redundant.
 */
export const CurveFitNotes = ({ curveState }: CurveFitNotesProps): JSX.Element | null => {
	const { t } = useGlobalization();
	const isWeibull3ParamEnabled = !!useIsFeatureEnabled('info360-weibull-3-param');

	// The message names the 3-parameter fit, while `selectedFormulationUnavailable` is a symmetric
	// test, so it is only the right wording while 3P is the selection.
	const showTwoParameterFallback =
		isWeibull3ParamEnabled &&
		curveState.selectedFormulationUnavailable &&
		curveState.weibullFormulation === WeibullFormulation.THREE_PARAMETER;

	if (curveState.noCurveFitted) return null;

	// Why the backend declined, when it said (I360A-2882). Null covers both a group trained before
	// that change and a reason this build does not recognise; both keep the original wording, which
	// described every decline as an unavailability because no better information existed.
	const declineNote = getWeibullDeclineNote(curveState.selectedFormulationDeclineReason, t);

	// Each reason's message is a whole sentence, used verbatim. Composing it here from a shared lead
	// would put the join in code that cannot know the target language's punctuation conventions.
	const fallbackMessage =
		declineNote?.message ??
		t(
			// eslint-disable-next-line quotes
			"Showing the 2-parameter Weibull curve — this asset group's failure data did not support a distinct 3-parameter fit.",
		);

	return (
		<Stack spacing={1}>
			{showTwoParameterFallback && (
				<Stack sx={{ mt: 1 }}>
					<WeibullFallbackBanner message={fallbackMessage} severity={declineNote?.severity} />
				</Stack>
			)}
			<WeibullFitNotes isFlat={curveState.selectedFitDegenerate} params={curveState.selectedFormulationParams} />
		</Stack>
	);
};
